import React, { useState, useEffect } from "react";
import { db, storage } from "../../../firebase";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
  deleteObject,
} from "firebase/storage";
import {
  addDoc,
  collection,
  serverTimestamp,
  deleteDoc,
  doc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  Modal, 
  Button, 
  Form, 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Spinner,
  InputGroup,
  FormControl,
  Badge,
  Tabs,
  Tab,
  Dropdown,
  Image
} from "react-bootstrap";
import { 
  FaEdit, 
  FaTrash, 
  FaSearch, 
  FaPlus, 
  FaImage, 
  FaNewspaper, 
  FaSave,
  FaEye,
  FaList,
  FaTimes,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaDownload,
  FaSyncAlt
} from "react-icons/fa";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";

const AddPost = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("add");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewContent, setPreviewContent] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [selectedPost, setSelectedPost] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    // Filter posts based on search term
    if (posts.length > 0) {
      if (searchTerm) {
        const filtered = posts.filter(post => 
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stripHtmlTags(post.content).toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredPosts(filtered);
      } else {
        setFilteredPosts(posts);
      }
    }
  }, [searchTerm, posts]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const postsCollection = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(postsCollection);
      const postsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPosts(postsData);
      setFilteredPosts(postsData);
    } catch (error) {
      console.error("Error fetching posts: ", error);
      toast.error("Failed to fetch posts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size exceeds 5MB limit. Please choose a smaller image.");
        return;
      }
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG, GIF, or WEBP).");
        return;
      }
      
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleImageDelete = () => {
    setImage(null);
    setImagePreview(null);
  };

  const viewPostDetails = (post) => {
    setSelectedPost(post);
    setShowDetailsModal(true);
  };

  const handleEdit = async (postId) => {
    resetForm();
    setCurrentPostId(postId);
    const postToEdit = posts.find((post) => post.id === postId);
    if (postToEdit) {
      setTitle(postToEdit.title);
      setContent(postToEdit.content);
      setImagePreview(postToEdit.imageUrl || null);
      setActiveTab("add");
      window.scrollTo(0, 0);
    }
  };

  const handleDelete = async (postId, imageUrl) => {
    if (window.confirm("Are you sure you want to delete this blog post?")) {
      setDelLoading(true);
      try {
        const postRef = doc(db, "blogs", postId);
        const postDoc = await getDoc(postRef);
        if (!postDoc.exists()) {
          throw new Error("Document does not exist");
        }

        // Delete the Firestore document
        await deleteDoc(postRef);

        // Delete the image from Storage if it exists
        if (imageUrl) {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        }

        toast.success("Blog post deleted successfully!");
        fetchPosts();
      } catch (error) {
        console.error("Error deleting post: ", error);
        toast.error("Failed to delete post. Please try again.");
      } finally {
        setDelLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!title.trim()) {
      toast.error("Please enter a title for the blog post.");
      return;
    }
    
    if (!content.trim() || content === '<p><br></p>') {
      toast.error("Please enter content for the blog post.");
      return;
    }
    
    setSubmitLoading(true);
    let imageUrl = imagePreview; // Default to the existing image

    try {
      // If a new image is selected, upload it
      if (image) {
        const fileName = `blog_${Date.now()}_${image.name}`;
        const imageRef = ref(storage, `blog_images/${fileName}`);
        const uploadTask = uploadBytesResumable(imageRef, image);

        await uploadTask;
        imageUrl = await getDownloadURL(imageRef);
      }

      // Handle update or create
      if (currentPostId) {
        const postRef = doc(db, "blogs", currentPostId);
        await updateDoc(postRef, {
          title,
          content,
          imageUrl: imageUrl || null,
          updatedAt: serverTimestamp(),
        });
        toast.success("Blog post updated successfully!");
      } else {
        await addDoc(collection(db, "blogs"), {
          title,
          content,
          imageUrl: imageUrl || null,
          createdAt: serverTimestamp(),
        });
        toast.success("Blog post created successfully!");
      }

      // Reset form and refresh posts
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error("Error adding/updating blog post: ", error);
      toast.error("Error saving blog post. Please try again later.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setContent("");
    setImage(null);
    setImagePreview(null);
    setCurrentPostId(null);
    setPreviewContent(false);
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) {
      return text;
    }
    return text.substr(0, maxLength) + "...";
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  // Custom toolbar for ReactQuill
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ color: [] }, { background: [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'link', 'image',
    'color', 'background'
  ];

  // Export to JSON function
  const handleExportData = () => {
    try {
      const jsonData = JSON.stringify(filteredPosts, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `blog_posts_export_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      URL.revokeObjectURL(url);
      toast.success("Blog posts exported successfully");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export blog data");
    }
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4"
              fill
            >
              <Tab 
                eventKey="add" 
                title={
                  <span>
                    <FaPlus className="me-2" />
                    {currentPostId ? "Edit Post" : "Add New Post"}
                  </span>
                }
              >
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 font-weight-bold text-primary">
                        <FaNewspaper className="me-2" />
                        {currentPostId ? "Edit Blog Post" : "Create New Blog Post"}
                      </h5>
                      <div>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          className="me-2"
                          onClick={resetForm}
                        >
                          <FaTimes className="me-1" /> Reset Form
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => setPreviewContent(!previewContent)}
                        >
                          <FaEye className="me-1" /> {previewContent ? "Hide Preview" : "Show Preview"}
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col lg={previewContent ? 6 : 12}>
                        <Form onSubmit={handleSubmit}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <strong>Post Title</strong>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                              placeholder="Enter blog post title"
                              required
                            />
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <strong>Post Content</strong>
                            </Form.Label>
                            <div className="quill-wrapper">
                              <ReactQuill
                                value={content}
                                onChange={setContent}
                                modules={modules}
                                formats={formats}
                                style={{ minHeight: "300px" }}
                              />
                            </div>
                          </Form.Group>
                          
                          <Form.Group className="mb-4">
                            <Form.Label>
                              <strong>Featured Image</strong>
                            </Form.Label>
                            <div className="input-group">
                              <div className="form-control bg-light d-flex align-items-center">
                                <span className="text-muted me-2">
                                  {image ? image.name : "No file selected"}
                                </span>
                              </div>
                              <label className="input-group-text btn btn-outline-primary" htmlFor="inputGroupFile">
                                <FaImage className="me-2" /> Browse
                              </label>
                              <input
                                type="file"
                                className="d-none"
                                id="inputGroupFile"
                                onChange={handleImageChange}
                                accept="image/*"
                              />
                            </div>
                            <Form.Text className="text-muted">
                              Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB
                            </Form.Text>
                            
                            {imagePreview && (
                              <div className="mt-3 position-relative d-inline-block">
                                <Image
                                  src={imagePreview}
                                  alt="Post image preview"
                                  rounded
                                  style={{ maxWidth: "100%", maxHeight: "200px" }}
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="position-absolute top-0 end-0 rounded-circle"
                                  style={{ margin: "5px" }}
                                  onClick={handleImageDelete}
                                >
                                  <FaTimes />
                                </Button>
                              </div>
                            )}
                          </Form.Group>
                          
                          <div className="d-flex justify-content-end">
                            <Button 
                              variant="secondary" 
                              className="me-2"
                              onClick={resetForm}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="primary" 
                              type="submit"
                              disabled={submitLoading}
                            >
                              {submitLoading ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <FaSave className="me-2" />
                                  {currentPostId ? "Update Post" : "Save Post"}
                                </>
                              )}
                            </Button>
                          </div>
                        </Form>
                      </Col>
                      
                      {previewContent && (
                        <Col lg={6}>
                          <div className="border rounded p-4 h-100">
                            <h4 className="mb-4 pb-2 border-bottom">Content Preview</h4>
                            <div className="preview-image mb-4 text-center">
                              {imagePreview ? (
                                <Image 
                                  src={imagePreview} 
                                  alt="Preview" 
                                  fluid 
                                  style={{ maxHeight: "250px" }}
                                />
                              ) : (
                                <div className="bg-light rounded p-5 d-flex align-items-center justify-content-center">
                                  <div className="text-muted">
                                    <FaImage size={32} className="mb-3" />
                                    <p>No featured image selected</p>
                                  </div>
                                </div>
                              )}
                            </div>
                            <h1 className="preview-title mb-4">
                              {title || "Post Title Preview"}
                            </h1>
                            <div className="preview-content">
                              {content ? (
                                <div 
                                  dangerouslySetInnerHTML={{ __html: content }} 
                                  className="ql-editor"
                                />
                              ) : (
                                <p className="text-muted">Start writing to see a preview of your content here.</p>
                              )}
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              </Tab>
              
              <Tab 
                eventKey="manage" 
                title={
                  <span>
                    <FaList className="me-2" />
                    Manage Posts
                  </span>
                }
              >
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0 font-weight-bold text-primary">
                        <FaNewspaper className="me-2" /> Blog Posts
                      </h5>
                      {loading ? (
                        <Spinner animation="border" size="sm" className="ms-3" />
                      ) : (
                        <Badge bg="info" className="ms-2">
                          {filteredPosts.length}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="d-flex">
                      <InputGroup className="me-3" style={{ width: "250px" }}>
                        <InputGroup.Text>
                          <FaSearch />
                        </InputGroup.Text>
                        <FormControl
                          placeholder="Search posts..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                      
                      <Dropdown className="me-2">
                        <Dropdown.Toggle variant="outline-primary" id="dropdown-actions">
                          Actions
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => {
                            resetForm();
                            setActiveTab("add");
                          }}>
                            <FaPlus className="me-2" /> Add New Post
                          </Dropdown.Item>
                          <Dropdown.Item onClick={handleExportData}>
                            <FaDownload className="me-2" /> Export Data
                          </Dropdown.Item>
                          <Dropdown.Item onClick={fetchPosts}>
                            <FaSyncAlt className="me-2" /> Refresh Data
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      
                      <div className="btn-group" role="group">
                        <Button
                          variant={viewMode === "table" ? "primary" : "outline-primary"}
                          onClick={() => setViewMode("table")}
                        >
                          <i className="fas fa-table me-1"></i> Table
                        </Button>
                        <Button
                          variant={viewMode === "cards" ? "primary" : "outline-primary"}
                          onClick={() => setViewMode("cards")}
                        >
                          <i className="fas fa-th me-1"></i> Cards
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  
                  <Card.Body className="p-0">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading blog posts...</p>
                      </div>
                    ) : filteredPosts.length === 0 ? (
                      <div className="text-center py-5 empty-state">
                        <FaExclamationTriangle size={48} className="text-muted mb-3" />
                        <h5>No blog posts found</h5>
                        <p className="text-muted">
                          {searchTerm ? 
                            "No posts match your search criteria." : 
                            "There are no blog posts. Create your first post now!"
                          }
                        </p>
                        {searchTerm ? (
                          <Button variant="outline-primary" onClick={() => setSearchTerm("")}>
                            Clear Search
                          </Button>
                        ) : (
                          <Button 
                            variant="primary" 
                            onClick={() => {
                              resetForm();
                              setActiveTab("add");
                            }}
                          >
                            <FaPlus className="me-2" /> Create First Post
                          </Button>
                        )}
                      </div>
                    ) : viewMode === "table" ? (
                      <div className="table-responsive">
                        <Table className="align-middle table-hover mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th>Title</th>
                              <th>Content Preview</th>
                              <th>Featured Image</th>
                              <th>Date</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredPosts.map((post) => (
                              <tr key={post.id}>
                                <td>
                                  <div className="fw-bold" style={{ maxWidth: "200px" }}>
                                    {truncateText(post.title, 50)}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-muted" style={{ maxWidth: "300px" }}>
                                    {truncateText(stripHtmlTags(post.content), 80)}
                                  </div>
                                </td>
                                <td>
                                  {post.imageUrl ? (
                                    <Image
                                      src={post.imageUrl}
                                      alt={post.title}
                                      width={60}
                                      height={60}
                                      className="rounded"
                                      style={{ objectFit: "cover" }}
                                    />
                                  ) : (
                                    <span className="text-muted">No image</span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <small>
                                      <FaCalendarAlt className="me-1 text-muted" />
                                      {formatDate(post.createdAt)}
                                    </small>
                                    {post.updatedAt && (
                                      <small className="text-muted">
                                        Updated: {formatDate(post.updatedAt)}
                                      </small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex justify-content-center">
                                    <Button 
                                      variant="outline-info" 
                                      size="sm" 
                                      className="me-2"
                                      title="View Details"
                                      onClick={() => viewPostDetails(post)}
                                    >
                                      <FaEye />
                                    </Button>
                                    <Button 
                                      variant="outline-primary" 
                                      size="sm" 
                                      className="me-2"
                                      title="Edit Post"
                                      onClick={() => handleEdit(post.id)}
                                    >
                                      <FaEdit />
                                    </Button>
                                    <Button 
                                      variant="outline-danger" 
                                      size="sm"
                                      title="Delete Post"
                                      onClick={() => handleDelete(post.id, post.imageUrl)}
                                      disabled={delLoading}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      // Card View
                      <div className="p-3">
                        <Row>
                          {filteredPosts.map((post) => (
                            <Col lg={4} md={6} className="mb-4" key={post.id}>
                              <Card className="h-100 shadow-sm hover-shadow">
                                <div style={{ height: "180px", overflow: "hidden" }}>
                                  {post.imageUrl ? (
                                    <Card.Img
                                      variant="top"
                                      src={post.imageUrl}
                                      alt={post.title}
                                      style={{ 
                                        width: "100%", 
                                        height: "100%", 
                                        objectFit: "cover" 
                                      }}
                                    />
                                  ) : (
                                    <div className="bg-light h-100 d-flex align-items-center justify-content-center">
                                      <div className="text-muted text-center">
                                        <FaImage size={32} className="mb-2" />
                                        <p>No featured image</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <Card.Body>
                                  <Card.Title className="text-truncate">
                                    {post.title}
                                  </Card.Title>
                                  <Card.Text className="text-muted mb-3" style={{ height: "48px", overflow: "hidden" }}>
                                    {truncateText(stripHtmlTags(post.content), 100)}
                                  </Card.Text>
                                  <small className="text-muted d-block mb-3">
                                    <FaCalendarAlt className="me-1" />
                                    {formatDate(post.createdAt)}
                                  </small>
                                  <div className="d-flex justify-content-between">
                                    <Button 
                                      variant="outline-info" 
                                      size="sm"
                                      onClick={() => viewPostDetails(post)}
                                    >
                                      <FaEye className="me-1" /> View
                                    </Button>
                                    <div>
                                      <Button 
                                        variant="outline-primary" 
                                        size="sm" 
                                        className="me-2"
                                        onClick={() => handleEdit(post.id)}
                                      >
                                        <FaEdit className="me-1" /> Edit
                                      </Button>
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => handleDelete(post.id, post.imageUrl)}
                                        disabled={delLoading}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Container>
        </main>
      </div>

      {/* Post Details Modal */}
      <Modal 
        show={showDetailsModal} 
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <FaNewspaper className="me-2 text-primary" />
            Blog Post Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedPost && (
            <div>
              {selectedPost.imageUrl && (
                <div className="text-center bg-light p-3">
                  <img 
                    src={selectedPost.imageUrl} 
                    alt={selectedPost.title}
                    className="img-fluid"
                    style={{ maxHeight: "300px" }}
                  />
                </div>
              )}
              
              <div className="p-4">
                <h1 className="mb-3">{selectedPost.title}</h1>
                
                <div className="d-flex mb-4">
                  <Badge bg="info" className="me-2 p-2">
                    <FaCalendarAlt className="me-1" />
                    {formatDate(selectedPost.createdAt)}
                  </Badge>
                  
                  {selectedPost.updatedAt && (
                    <Badge bg="secondary" className="p-2">
                      Updated: {formatDate(selectedPost.updatedAt)}
                    </Badge>
                  )}
                </div>
                
                <div className="border-top pt-4">
                  <div 
                    dangerouslySetInnerHTML={{ __html: selectedPost.content }} 
                    className="ql-editor"
                  />
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          {selectedPost && (
            <Button 
              variant="primary" 
              onClick={() => {
                setShowDetailsModal(false);
                handleEdit(selectedPost.id);
              }}
            >
              <FaEdit className="me-2" />
              Edit Post
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddPost;